import { useCallback } from 'react';
import { doc, setDoc, getDoc } from '@/lib/firebase/firestore';
import { deleteField } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getEffectiveUid } from '@/stores/auth-store';
import { useAppStore } from '@/stores/app-store';
import { useSettingsStore } from '@/stores/settings-store';
import type { MessageTemplate } from '@/types/messaging';

const TEMPLATE_PREFIX = 'ua_msg_template_item_';

/**
 * Get the Firestore document path for org-level settings.
 * Path: uaAppPreferences/{saasCode}_orgs/{orgId}/{extensionName}
 */
function getOrgDocPath(): string | null {
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;
  if (!extensionName || !orgId || !saasCode) return null;
  return `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}`;
}

/**
 * Get the Firestore document path for personal settings.
 * Path: uaAppPreferences/{saasCode}_orgs/{orgId}/{extensionName}/personal/{uid}
 */
function getPersonalDocPath(): string | null {
  const uid = getEffectiveUid();
  const orgPath = getOrgDocPath();
  if (!uid || !orgPath) return null;
  return `${orgPath}/personal/${uid}`;
}

/** Extract {{variable}} placeholders from template body */
function extractVariables(body: string): string[] {
  const matches = body.match(/\{\{(\w+)\}\}/g);
  if (!matches) return [];
  const vars = new Set(matches.map((m) => m.replace(/\{\{|\}\}/g, '')));
  return Array.from(vars);
}

/**
 * Re-read templates from Firestore after a save/delete, then update the store.
 * Only called after mutations — normal reads use the already-loaded store data.
 */
async function refreshTemplatesFromFirestore(): Promise<MessageTemplate[]> {
  const orgPath = getOrgDocPath();
  const personalPath = getPersonalDocPath();
  if (!orgPath) return [];

  const all: MessageTemplate[] = [];

  const orgSnap = await getDoc(doc(db, orgPath));
  if (orgSnap.exists()) {
    const data = orgSnap.data();
    for (const [key, value] of Object.entries(data)) {
      if (!key.startsWith(TEMPLATE_PREFIX)) continue;
      const tmpl = value as { title?: string; message?: string; saasModule?: string };
      if (!tmpl?.title || !tmpl?.message) continue;
      const t: MessageTemplate = {
        id: key,
        name: tmpl.title,
        body: tmpl.message,
        channel: 'SMS',
        category: 'Shared',
        status: 'approved',
        variables: extractVariables(tmpl.message),
      };
      if (tmpl.saasModule) (t as Record<string, unknown>).saasModule = tmpl.saasModule;
      all.push(t);
    }
  }

  if (personalPath) {
    const personalSnap = await getDoc(doc(db, personalPath));
    if (personalSnap.exists()) {
      const data = personalSnap.data();
      for (const [key, value] of Object.entries(data)) {
        if (!key.startsWith(TEMPLATE_PREFIX)) continue;
        const tmpl = value as { title?: string; message?: string; saasModule?: string };
        if (!tmpl?.title || !tmpl?.message) continue;
        const t: MessageTemplate = {
          id: key,
          name: tmpl.title,
          body: tmpl.message,
          channel: 'SMS',
          category: 'Personal',
          status: 'approved',
          variables: extractVariables(tmpl.message),
        };
        if (tmpl.saasModule) (t as Record<string, unknown>).saasModule = tmpl.saasModule;
        all.push(t);
      }
    }
  }

  // Deduplicate — personal overrides org
  const seen = new Map<string, MessageTemplate>();
  for (const t of all) seen.set(t.id, t);
  const merged = Array.from(seen.values());

  // Update settings store so all consumers see fresh data
  useSettingsStore.getState().setSavedTemplates(merged);
  return merged;
}

/**
 * Hook for managing user-created saved templates (stored in Firebase).
 * These are available for ALL channels (SMS, WhatsApp, etc.).
 *
 * Templates are extracted from the personal + org docs that are already
 * loaded by loadAllSettings() — no extra Firestore reads on mount.
 * Only save/delete mutations trigger Firestore writes + re-read.
 */
export function useSavedTemplates() {
  const templates = useSettingsStore((s) => s.savedTemplates);
  const savedTemplatesLoaded = useSettingsStore((s) => s.savedTemplatesLoaded);
  const settingsLoaded = useSettingsStore((s) => s.settingsLoaded);
  // Templates are "loading" only when neither savedTemplatesLoaded nor settingsLoaded is true.
  // This prevents the loading spinner from getting stuck if savedTemplatesLoaded was never
  // explicitly set (e.g. loadAllSettings errored before reaching that point).
  const loading = !savedTemplatesLoaded && !settingsLoaded;

  const saveTemplate = useCallback(async (
    name: string,
    message: string,
    visibility: 'all_users' | 'only_me' = 'all_users',
    saasModule?: string
  ): Promise<void> => {
    const key = `${TEMPLATE_PREFIX}${name}`;
    const templateData: Record<string, unknown> = {
      title: name,
      message,
      visibility,
      createdTime: Date.now(),
    };
    const uid = getEffectiveUid();
    if (uid) templateData.createdBy_UA = uid;
    if (saasModule) templateData.saasModule = saasModule;

    const path = visibility === 'only_me' ? getPersonalDocPath() : getOrgDocPath();
    if (!path) throw new Error('Missing app context');

    await setDoc(doc(db, path), { [key]: templateData }, { merge: true });
    await refreshTemplatesFromFirestore();
  }, []);

  const deleteTemplate = useCallback(async (templateId: string): Promise<void> => {
    const orgPath = getOrgDocPath();
    const personalPath = getPersonalDocPath();

    const promises: Promise<void>[] = [];
    if (orgPath) {
      promises.push(
        setDoc(doc(db, orgPath), { [templateId]: deleteField() }, { merge: true })
      );
    }
    if (personalPath) {
      promises.push(
        setDoc(doc(db, personalPath), { [templateId]: deleteField() }, { merge: true })
      );
    }

    await Promise.allSettled(promises);
    await refreshTemplatesFromFirestore();
  }, []);

  return {
    templates,
    loading,
    error: null as string | null,
    refresh: refreshTemplatesFromFirestore,
    saveTemplate,
    deleteTemplate,
  };
}
